import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, MapPin, Ticket, Bookmark, Check, X, QrCode } from "lucide-react";
import { formatDate, eventPriceRange } from "@/lib/format";
import EventPassDialog from "@/components/EventPassDialog";

export default function FanEvents() {
    const { refresh, cancelRegistration } = useFan();
    const [data, setData] = useState({ upcoming: [], attended: [], saved: [] });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");
    const [passRegId, setPassRegId] = useState(null);
    const [passOpen, setPassOpen] = useState(false);

    const openPass = (regId) => {
        setPassRegId(regId);
        setPassOpen(true);
    };

    const load = () => {
        setLoading(true);
        endpoints.fanEvents().then((d) => {
            setData(d);
            setLoading(false);
        }).catch(() => setLoading(false));
    };
    useEffect(load, []);

    const unsave = async (event_id) => {
        try {
            await endpoints.fanUnsaveEvent(event_id);
            toast("Removed from saved");
            await refresh();
            load();
        } catch {
            toast.error("Could not remove");
        }
    };

    const registerFromSaved = async (event_id) => {
        try {
            await endpoints.fanRegister(event_id);
            toast.success("Registered");
            await refresh();
            load();
        } catch {
            toast.error("Registration failed");
        }
    };

    const cancelUpcoming = async (event_id) => {
        if (!window.confirm("Cancel your registration? Your Event Pass will no longer be valid.")) return;
        try {
            await cancelRegistration(event_id);
            toast.success("Your registration has been cancelled.");
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Cancellation failed";
            toast.error(msg);
        }
    };

    return (
        <div className="p-4 md:p-6" data-testid="fan-events-page">
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-transparent border border-[#2a2a2a] rounded-none p-0 h-auto">
                    <TabsTrigger value="upcoming" data-testid="fan-events-tab-upcoming" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">
                        Upcoming ({data.upcoming.length})
                    </TabsTrigger>
                    <TabsTrigger value="attended" data-testid="fan-events-tab-attended" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">
                        Attended ({data.attended.length})
                    </TabsTrigger>
                    <TabsTrigger value="saved" data-testid="fan-events-tab-saved" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">
                        Saved ({data.saved.length})
                    </TabsTrigger>
                </TabsList>

                {loading && <div className="mt-6 font-mono text-sm text-[#666]" data-testid="fan-events-loading">Loading…</div>}

                <TabsContent value="upcoming" className="mt-6">
                    {data.upcoming.length === 0 ? (
                        <Empty
                            icon={CalendarDays}
                            text="No upcoming events yet. Register from any event page."
                            action={<Link to="/events" className="pm-btn pm-btn-primary" data-testid="fan-events-browse-upcoming">Browse events</Link>}
                            testId="fan-events-empty-upcoming"
                        />
                    ) : (
                        <EventList events={data.upcoming} status="upcoming" onOpenPass={openPass} onCancel={cancelUpcoming} />
                    )}
                </TabsContent>

                <TabsContent value="attended" className="mt-6">
                    {data.attended.length === 0 ? (
                        <Empty
                            icon={Check}
                            text="Get checked in at an event and it lands here automatically."
                            testId="fan-events-empty-attended"
                        />
                    ) : (
                        <EventList events={data.attended} status="attended" onOpenPass={openPass} />
                    )}
                </TabsContent>

                <TabsContent value="saved" className="mt-6">
                    {data.saved.length === 0 ? (
                        <Empty
                            icon={Bookmark}
                            text="Nothing saved yet. Hit 'Save / Interested' on any event you're eyeing."
                            testId="fan-events-empty-saved"
                        />
                    ) : (
                        <EventList
                            events={data.saved}
                            status="saved"
                            actions={(e) => (
                                <>
                                    <button onClick={() => registerFromSaved(e.id)} className="pm-btn pm-btn-primary text-xs" data-testid={`fan-saved-register-${e.id}`}>
                                        <Ticket className="w-3 h-3 mr-1" /> Register
                                    </button>
                                    <button onClick={() => unsave(e.id)} className="pm-btn text-xs" data-testid={`fan-saved-unsave-${e.id}`}>
                                        <X className="w-3 h-3 mr-1" /> Remove
                                    </button>
                                </>
                            )}
                        />
                    )}
                </TabsContent>
            </Tabs>
            <EventPassDialog registrationId={passRegId} open={passOpen} onOpenChange={setPassOpen} />
        </div>
    );
}

function EventList({ events, status, actions, onOpenPass, onCancel }) {
    return (
        <div className="border border-[#2a2a2a]">
            {events.map((e) => (
                <div key={e.id} className="flex flex-col md:flex-row items-stretch border-b border-[#2a2a2a] last:border-b-0" data-testid={`fan-event-row-${e.id}`}>
                    <Link to={`/events/${e.id}`} className="relative w-full md:w-64 aspect-[16/9] md:aspect-auto md:h-40 overflow-hidden shrink-0 group">
                        <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                    </Link>
                    <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase text-[#ff4400]">{e.artist_name}</div>
                            <div className="font-display font-black uppercase text-xl leading-tight mt-1">{e.name}</div>
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#a3a3a3]">
                                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {formatDate(e.date)} · {e.start_time}</span>
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue_name} · {e.city}</span>
                            </div>
                        </div>
                        <div className="flex flex-col md:items-end gap-2">
                            <div className="font-display font-black">{eventPriceRange(e)}</div>
                            <StatusPill status={status} registration={e.registration} isFree={e.is_free} />
                            {onOpenPass && e.registration?.id && (status === "upcoming" || status === "attended") && (
                                <button
                                    onClick={() => onOpenPass(e.registration.id)}
                                    className="pm-btn pm-btn-primary text-xs"
                                    data-testid={`fan-view-pass-${e.id}`}
                                >
                                    <QrCode className="w-3 h-3 mr-1" /> View Event Pass
                                </button>
                            )}
                            {onCancel && status === "upcoming" && (
                                <button
                                    type="button"
                                    onClick={() => onCancel(e.id)}
                                    className="text-[10px] font-mono uppercase tracking-widest text-[#666] hover:text-[#ff3333] underline underline-offset-4 transition-colors"
                                    data-testid={`fan-cancel-registration-${e.id}`}
                                >
                                    Cancel registration
                                </button>
                            )}
                            {actions && (
                                <div className="flex gap-2">
                                    {actions(e)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function StatusPill({ status, registration, isFree }) {
    if (status === "attended") {
        return <span className="text-[10px] font-mono uppercase px-2 py-1 border border-[#00e676] text-[#00e676]">Checked in</span>;
    }
    if (status === "saved") {
        return <span className="text-[10px] font-mono uppercase px-2 py-1 border border-[#ffea00] text-[#ffea00]">Interested</span>;
    }
    if (status === "upcoming") {
        const label = isFree ? "Registered" : registration?.payment_status === "paid" ? "Paid" : registration?.payment_status === "pay_at_door" ? "Pay at door" : "Registered";
        return <span className="text-[10px] font-mono uppercase px-2 py-1 border border-[#ff4400] text-[#ff4400]">{label}</span>;
    }
    return null;
}

function Empty({ icon: Icon, text, action, testId }) {
    return (
        <div className="border border-dashed border-[#2a2a2a] p-10 text-center flex flex-col items-center gap-3" data-testid={testId}>
            <Icon className="w-8 h-8 text-[#666]" />
            <div className="text-sm text-[#a3a3a3]">{text}</div>
            {action}
        </div>
    );
}
